import React from 'react';
import { resolveUrl } from '../api';
import { useCMS } from '../context/CMSContext';

// Treat legacy peptihub-CDN URLs as "no image" so the placeholder shows.
const PLACEHOLDER_PATTERNS = ['fwqslizxtpwevlwd', 'peptihub.com/images/product-placeholder'];
const hasRealImage = (url) =>
  !!url && !PLACEHOLDER_PATTERNS.some((p) => url.toLowerCase().includes(p));

/**
 * Clean text-based product placeholder shown when no photo has been uploaded.
 * Subtle gradient panel with the product name in bold typography.
 */
export const ProductPlaceholder = ({ name = 'Product', subtitle = '99% HPLC', className = '' }) => {
  const { t } = useCMS();
  const brandName = (t('brand.name', 'FAT BUSTERS') || 'FAT BUSTERS').toUpperCase();
  // Try to split "GHK-Cu 50mg" → main = "GHK-Cu", size = "50mg"
  const match = (name || '').match(/^(.*?)\s*(\d+\s*(?:mg|ml|mcg|g|iu)\b.*)$/i);
  const main = match ? match[1].trim() : name;
  const size = match ? match[2].trim() : null;

  return (
    <div className={`relative w-full h-full flex flex-col items-center justify-center text-center bg-gradient-to-br from-red-50 via-white to-slate-50 ${className}`}>
      <div className="px-4">
        <div className="text-[10px] font-bold tracking-[0.22em] text-red-600 mb-3">{brandName}</div>
        <div className="text-[#0a0a0a] font-extrabold leading-tight" style={{ fontSize: 'clamp(1.15rem, 4.5vw, 1.9rem)' }}>
          {main}
        </div>
        {size && (
          <div className="mt-1 text-slate-500 font-semibold" style={{ fontSize: 'clamp(0.85rem, 3vw, 1.1rem)' }}>
            {size}
          </div>
        )}
        <div className="mt-4 inline-block text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-400 border-t border-slate-200 pt-2">
          {subtitle}
        </div>
      </div>
    </div>
  );
};

/** Renders an uploaded/external product image, or the clean placeholder. */
export const ProductImage = ({ product, className = '', containerClassName = '' }) => {
  if (hasRealImage(product?.image)) {
    return (
      <img
        src={resolveUrl(product.image)}
        alt={product.name || 'Product'}
        loading="lazy"
        className={className}
      />
    );
  }
  return (
    <div className={containerClassName || className}>
      <ProductPlaceholder name={product?.name || 'Product'} subtitle={product?.purity || '99% HPLC'} />
    </div>
  );
};

export { hasRealImage };
export default ProductPlaceholder;
